// services/invitationDraft.js
//
// The half-built invitation — or card — kept across a sign-in.
//
// ── Why this exists ────────────────────────────────────────────────────────
//
// Creating an invitation no longer asks for an account first. Someone can pick
// a cover, name the party, set a date and see the thing take shape before the
// product asks them for anything — which is the order Partiful uses and the
// order that makes a visitor into a host.
//
// The account still has to exist by the time it is published: `events.host_id`
// is not nullable and the row-level policy checks it, so there is no version
// of this where an anonymous insert succeeds. What changes is WHEN the ask
// happens — at the last step, on work already done, instead of at the first.
// That only works if the work survives the round trip through /login, and this
// is what makes it survive.
//
// ── Where it is kept ───────────────────────────────────────────────────────
//
// localStorage, not sessionStorage: signing in with an email link or an OAuth
// provider can land the person back in a different tab, and sessionStorage is
// per-tab. It is cleared as soon as the invitation is published, and it
// expires on its own after a week so an abandoned draft does not haunt a
// shared computer.
//
// ── The cover ──────────────────────────────────────────────────────────────
//
// An uploaded cover arrives as a base64 data URL, which for a photo off a
// phone is comfortably past what localStorage will hold (browsers give the
// origin about 5MB, total). So the draft has a budget: over it, the cover is
// dropped and everything else is kept, and `coverDropped` tells the page to
// say so rather than silently reverting to a stock image the host did not
// choose.

/**
 * One key per kind of thing being made.
 *
 * Invitations and cards are built on different pages and can be abandoned
 * independently, so they get a slot each — a single key would mean starting a
 * card silently discarded a half-finished invitation.
 */
const KEYS = {
  invitation: 'invits.invitationDraft.v1',
  card: 'invits.cardDraft.v1',
};

const keyFor = (kind) => KEYS[kind] ?? KEYS.invitation;

/** A week. Long enough to come back to, short enough not to linger. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Roughly 2MB of JSON. localStorage quota is counted in UTF-16 code units, so
 * the real ceiling is about double this; staying well under leaves room for
 * whatever else the origin has stored.
 */
const MAX_CHARS = 2_000_000;

const isDataUrl = (value) => typeof value === 'string' && value.startsWith('data:');

/**
 * Store a draft. Returns what happened, so the caller can tell the person.
 *
 * @param {object} draft
 * @returns {{ok: boolean, coverDropped: boolean}}
 */
export function saveDraft(draft, kind = 'invitation') {
  const key = keyFor(kind);
  const attempt = (payload) => {
    const json = JSON.stringify(payload);
    if (json.length > MAX_CHARS) return false;
    window.localStorage.setItem(key, json);
    return true;
  };

  const withTime = { ...draft, savedAt: Date.now() };

  try {
    if (attempt(withTime)) return { ok: true, coverDropped: false };

    // Too big. The cover is almost always the reason.
    const lean = {
      ...withTime,
      coverImage: isDataUrl(withTime.coverImage?.uri) ? null : withTime.coverImage,
      blurredBackground: isDataUrl(withTime.blurredBackground) ? null : withTime.blurredBackground,
      coverDropped: true,
    };
    if (attempt(lean)) return { ok: true, coverDropped: true };

    return { ok: false, coverDropped: false };
  } catch (err) {
    // QuotaExceededError, or storage disabled entirely (private mode, or a
    // browser configured to block it). Losing the draft is bad; throwing in
    // the middle of a publish is worse.
    console.warn('Could not keep the invitation draft:', err);
    return { ok: false, coverDropped: false };
  }
}

/** The stored draft, or null if there is none, it is stale, or it is corrupt. */
export function readDraft(kind = 'invitation') {
  try {
    const json = window.localStorage.getItem(keyFor(kind));
    if (!json) return null;

    const draft = JSON.parse(json);
    if (!draft || typeof draft !== 'object') return null;

    if (!draft.savedAt || Date.now() - draft.savedAt > MAX_AGE_MS) {
      clearDraft(kind);
      return null;
    }
    return draft;
  } catch (err) {
    console.warn('Ignoring an unreadable draft:', err);
    clearDraft(kind);
    return null;
  }
}

export function clearDraft(kind = 'invitation') {
  try {
    window.localStorage.removeItem(keyFor(kind));
  } catch {
    // Nothing to do, and nothing worth interrupting anyone over.
  }
}

/** Is there a draft worth offering to restore? */
export function hasDraft(kind = 'invitation') {
  return readDraft(kind) !== null;
}

/**
 * Dates do not survive JSON.
 *
 * The create form holds half a dozen Date objects (start, end, RSVP
 * deadline…). JSON.stringify turns each into an ISO string and JSON.parse
 * leaves it a string, so every `startTime.getHours()` on the other side of a
 * sign-in would throw. Both directions go through here.
 */
export const toStored = (date) => (date instanceof Date ? date.toISOString() : null);

export const fromStored = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
